// src/pages/UserPages/UserMainPageContent.js

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
} from "@mui/material";
import debounce from "lodash.debounce";
import { useNavigate } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";

// Serviços e componentes personalizados
import axiosInstance from "../../Services/axiosConfig";
import Navbar from "../../components/Navbar";
import ServiceCard from "../../components/ServiceCard";
import FilterSection from "../../components/FilterSection";
import FeedbackSnackbar from "../../components/FeedbackSnackbar";
import LoadingIndicator from "../../components/LoadingIndicator";
import FeedbackButton from "../../components/FeedbackButton";
import { useThemeMode } from "../../components/ThemeModeContext";

function UserMainPageContent() {
  /** -------------------------------------------------------------------------
   * ESTADOS GERAIS
   * --------------------------------------------------------------------------*/
  const [services, setServices] = useState([]);
  const [filters, setFilters] = useState({
    service_name: "",
    sector_name: "",
    company_name: "",
    category: "",
    price: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  /** -------------------------------------------------------------------------
   * ESTADO PARA O SNACKBAR (FEEDBACK)
   * --------------------------------------------------------------------------*/
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  /** -------------------------------------------------------------------------
   * ESTADO PARA O NOME DE USUÁRIO (CASO PRECISE EXIBI-LO)
   * --------------------------------------------------------------------------*/
  const [email, setEmail] = useState(""); // Alterar para o campo correto
  const [username, setUsername] = useState("");


  /** -------------------------------------------------------------------------
   * OPÇÕES DE AUTOCOMPLETE
   * --------------------------------------------------------------------------*/
  const [sectors, setSectors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [companies, setCompanies] = useState([]);

  /** -------------------------------------------------------------------------
   * DETECTA O MODO (LIGHT OU DARK) PARA O TEMA
   * --------------------------------------------------------------------------*/
  const { mode } = useThemeMode();

  /** -------------------------------------------------------------------------
   * HOOK DE NAVEGAÇÃO (CASO PRECISE REDIRECIONAR)
   * --------------------------------------------------------------------------*/
  const navigate = useNavigate();

  /** -------------------------------------------------------------------------
   * CARREGA AS OPÇÕES DE FILTRO (SETORES, CATEGORIAS, EMPRESAS) NA MONTAGEM
   * --------------------------------------------------------------------------*/
  useEffect(() => {
    async function fetchOptions() {
      try {
        const [sectorsRes, categoriesRes, companiesRes] = await Promise.all([
          axiosInstance.get("/sectors/"),      // Endpoint para setores
          axiosInstance.get("/categories/"),   // Endpoint para categorias
          axiosInstance.get("/companies/"),    // Endpoint para empresas
        ]);

        setSectors(sectorsRes.data.data || []);
        setCategories(categoriesRes.data.data || []);
        setCompanies(companiesRes.data.data || []);
      } catch (err) {
        console.error("Erro ao buscar as opções de filtros:", err);
        setError("Ocorreu um erro ao buscar as opções de filtros.");
        showSnackbar("Ocorreu um erro ao buscar as opções de filtros.", "error");
      }
    }

    fetchOptions();
  }, []);

  /** -------------------------------------------------------------------------
   * CARREGA O NOME DO USUÁRIO NA MONTAGEM (CASO PRECISE EXIBI-LO)
   * --------------------------------------------------------------------------*/
  useEffect(() => {
    async function fetchUserName() {
      try {
        const response = await axiosInstance.get("/user/profile");
        if (response.data?.username) {
          setUsername(response.data.username);
        }
      } catch (err) {
        console.error("Erro ao buscar perfil do usuário:", err);
        // Tratamento de erro opcional
      }
    }

    fetchUserName();
  }, []);

  /** -------------------------------------------------------------------------
   * FUNÇÃO PARA EXIBIR O SNACKBAR
   * --------------------------------------------------------------------------*/
  const showSnackbar = (message, severity = "success") => {
    setSnackbarMsg(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  /** -------------------------------------------------------------------------
   * FUNÇÃO DE BUSCA DE FORMA DEBOUNCED
   * --------------------------------------------------------------------------*/
  const debouncedSearch = useMemo(
    () =>
      debounce(async (currentFilters) => {
        setLoading(true);

        try {
          const response = await axiosInstance.get("user-search/", {
            params: currentFilters,
          });

          if (response.data.status === "success") {
            const resultArray = Object.values(response.data.data);
            console.log(resultArray);
            setServices(resultArray);
            showSnackbar(`${resultArray.length} serviço(s) encontrado(s).`, "success");
          } else {
            setServices([]);
            showSnackbar("Nenhum serviço encontrado com os filtros aplicados.", "info");
          }
        } catch (err) {
          console.error("Erro ao buscar serviços:", err);
          setError("Ocorreu um erro ao buscar os serviços. Tente novamente.");
          showSnackbar("Ocorreu um erro ao buscar os serviços. Tente novamente.", "error");
        } finally {
          setLoading(false);
        }
      }, 500),
    []
  );

  /** -------------------------------------------------------------------------
   * DISPARA A BUSCA DE FORMA DEBOUNCED QUANDO OS FILTROS MUDAM
   * --------------------------------------------------------------------------*/
  useEffect(() => {
    debouncedSearch(filters);

    // Cleanup do debounce ao desmontar
    return () => {
      debouncedSearch.cancel();
    };
  }, [filters, debouncedSearch]);

  /** -------------------------------------------------------------------------
   * HANDLERS PARA MUDANÇA DE FILTROS
   * --------------------------------------------------------------------------*/
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleAutocompleteChange = (event, value, name) => {
    setFilters((prev) => ({ ...prev, [name]: value?.name || "" }));
  };

  /** -------------------------------------------------------------------------
   * FUNÇÕES DE AÇÃO
   * --------------------------------------------------------------------------*/
  const handleSearch = () => {
    // Força a busca imediata sem esperar o debounce,
    // caso o usuário clique em "Buscar"
    debouncedSearch.flush();
  };

  const handleClearFilters = () => {
    setFilters({
      service_name: "",
      sector_name: "",
      company_name: "",
      category: "",
      price: "",
    });
    setServices([]);
  };

  /** -------------------------------------------------------------------------
   * DEFINIÇÃO DO TEMA (LIGHT / DARK)
   * --------------------------------------------------------------------------*/
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          background: {
            default: mode === "light" ? "#FAF5E4" : "#121212",
            paper: mode === "light" ? "#FFFFFF" : "#1E1E1E",
          },
          text: {
            primary: mode === "light" ? "#1A202C" : "#FFFFFF",
          },
        },
      }),
    [mode]
  );

  /** -------------------------------------------------------------------------
   * RENDERIZAÇÃO
   * --------------------------------------------------------------------------*/
  return (
    <ThemeProvider theme={theme}>
      <Box
        minHeight="100vh"
        sx={{ bgcolor: "background.default", color: "text.primary" }}
      >
        <Navbar username={email} />

        <Box p={3}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              textAlign: "center",
              mb: 3,
              fontWeight: "bold",
            }}
          >

          </Typography>

          {/* Seção de Filtros */}
          <FilterSection
            filters={filters}
            handleChange={handleChange}
            sectors={sectors}
            categories={categories}
            companies={companies}
            handleAutocompleteChange={handleAutocompleteChange}
            handleSearchSubmit={handleSearch}  // Permite clicar em "Buscar" no FilterSection
          />

          {/* Botões de ação: Buscar e Limpar Filtros */}
          <Box display="flex" justifyContent="center" gap={2} mb={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={loading}
              sx={{ minWidth: "120px" }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Buscar"}
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClearFilters}
              disabled={loading}
              sx={{ minWidth: "120px" }}
            >
              Limpar Filtros
            </Button>
          </Box>

          {/* Lista de Serviços ou Estado de Carregamento */}
          {loading ? (
            <LoadingIndicator />
          ) : services.length > 0 ? (
            <>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ textAlign: "center", mb: 2 }}
              >
                {`${services.length} serviço(s) encontrado(s).`}
              </Typography>
              <Grid container spacing={3}>
                {services.map((service) => (
                  <Grid item xs={12} sm={6} md={4} key={service.id}>
                    <ServiceCard service={service} />
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <Typography
              variant="body1"
              sx={{ textAlign: "center", mt: 3 }}
            >
              Nenhum serviço encontrado com os critérios de busca.
            </Typography>
          )}
        </Box>

        {/* Snackbar para feedback geral */}
        <FeedbackSnackbar
          open={openSnackbar}
          handleClose={handleCloseSnackbar}
          message={snackbarMsg}
          severity={snackbarSeverity}
        />

        {/* Botão de Feedback (para mudar tema, etc.) */}
        <FeedbackButton />
      </Box>
    </ThemeProvider>
  );
}

export default UserMainPageContent;
